// Service Steady PWA — src/components/ReviewRequestSheet.tsx — 2026-07-02
// (DemoPolish 2)
//
// Real "Send review request" flow for the Today quick action (which used to
// just navigate to Chats — dead). Two ways to pick the recipient: a
// searchable list of recent customers (reuses the conversations the app
// already fetches) or a hand-entered phone number. This is a CUSTOMER-FACING
// SEND, so the actual send sits behind an explicit confirm step — no
// accidental sends. The send path is the existing Worker review-request
// mechanism (locked per-tenant wording + review URL); the manual-number path
// find-or-creates the contact Worker-side and reuses the same sender.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Star, X } from 'lucide-react';
import {
  fetchConversations,
  triggerReviewRequest,
  triggerReviewRequestByPhone,
} from '@/lib/api';

interface ReviewRequestSheetProps {
  slug: string;
  open: boolean;
  onClose: () => void;
}

interface Recipient {
  kind: 'contact' | 'phone';
  contactId?: string;
  phone: string;
  name: string;
}

function looksLikeUsPhone(raw: string): boolean {
  const digits = raw.replace(/\D/g, '');
  if (raw.trim().startsWith('+')) return digits.length >= 8 && digits.length <= 15;
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
}

export function ReviewRequestSheet({ slug, open, onClose }: ReviewRequestSheetProps) {
  const [search, setSearch] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [picked, setPicked] = useState<Recipient | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSearch('');
      setManualPhone('');
      setPicked(null);
      setSentTo(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Recent customers — the same conversations feed the app already uses.
  const recentQuery = useQuery({
    queryKey: ['review-recipients', slug],
    queryFn: () => fetchConversations({ slug, filter: 'all', limit: 50 }),
    enabled: open && Boolean(slug),
    staleTime: 60_000,
  });

  const recipients = useMemo<Recipient[]>(() => {
    const seen = new Set<string>();
    const out: Recipient[] = [];
    for (const c of recentQuery.data?.conversations ?? []) {
      if (!c.contact_id || seen.has(c.contact_id)) continue;
      seen.add(c.contact_id);
      out.push({
        kind: 'contact',
        contactId: c.contact_id,
        phone: c.contact_phone || '',
        name: c.contact_name || c.contact_phone || 'Unknown',
      });
    }
    const q = search.trim().toLowerCase();
    if (!q) return out;
    return out.filter(
      (r) => r.name.toLowerCase().includes(q) || r.phone.replace(/\D/g, '').includes(q.replace(/\D/g, '')),
    );
  }, [recentQuery.data, search]);

  const send = useMutation({
    mutationFn: async (r: Recipient) => {
      if (r.kind === 'contact' && r.contactId) {
        return triggerReviewRequest({ contactId: r.contactId, slug });
      }
      return triggerReviewRequestByPhone({ slug, phone: r.phone });
    },
    onSuccess: (_res, r) => setSentTo(r.name || r.phone),
  });

  if (!open) return null;

  const manualValid = looksLikeUsPhone(manualPhone);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Send review request"
      onClick={onClose}
    >
      <div
        className="flex w-full max-h-[85vh] max-w-md flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl dark:bg-[#0F172A] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 justify-center pt-2 sm:hidden" aria-hidden="true">
          <div className="h-1 w-9 rounded-full bg-ink-200 dark:bg-[#334155]" />
        </div>
        <div className="flex shrink-0 items-center justify-between border-b border-ink-100 px-4 py-3 dark:border-[#1F2A37]">
          <div className="flex items-center gap-2 text-base font-semibold text-ink-900 dark:text-white">
            <Star className="h-4 w-4 text-amber-500" aria-hidden="true" />
            Send review request
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full text-ink-500 hover:bg-ink-50 dark:text-[#94A3B8] dark:hover:bg-[#1E293B]"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div
          className="flex-1 overflow-y-auto px-4 py-3"
          style={{ paddingBottom: 'calc(var(--tab-h, 64px) + env(safe-area-inset-bottom, 0px))' }}
        >
          {sentTo ? (
            /* Success state */
            <div className="flex flex-col items-center py-8 text-center">
              <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400">
                <Check className="h-6 w-6" />
              </span>
              <div className="text-[15px] font-semibold text-ink-900 dark:text-[#E2E8F0]">
                Review request sent to {sentTo}
              </div>
              <p className="mt-1 text-xs text-ink-500 dark:text-[#94A3B8]">
                They just got your review text with the Google link.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="ss-press mt-5 rounded-xl px-6 py-2.5 text-sm font-semibold text-white"
                style={{ background: 'var(--ss-accent, #2563EB)' }}
              >
                Done
              </button>
            </div>
          ) : picked ? (
            /* Explicit confirm step — customer-facing send */
            <div className="py-2">
              <p className="text-[15px] text-ink-900 dark:text-[#E2E8F0]">
                Text a review request to{' '}
                <span className="font-semibold">{picked.name || picked.phone}</span>
                {picked.phone && picked.name !== picked.phone ? (
                  <span className="text-ink-500 dark:text-[#94A3B8]"> ({picked.phone})</span>
                ) : null}
                ?
              </p>
              <p className="mt-1.5 text-xs text-ink-500 dark:text-[#94A3B8]">
                Sends your standard review message with the Google review link.
              </p>
              {send.isError ? (
                <p className="mt-2 text-xs font-medium text-red-600 dark:text-red-400">
                  Couldn't send — please try again.
                </p>
              ) : null}
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => setPicked(null)}
                  disabled={send.isPending}
                  className="ss-press flex-1 rounded-xl border border-ink-200 bg-ink-50 px-3 py-2.5 text-center text-sm font-semibold text-ink-900 dark:border-[#334155] dark:bg-[#0F172A] dark:text-[#E2E8F0]"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => send.mutate(picked)}
                  disabled={send.isPending}
                  className="ss-press flex-1 rounded-xl px-3 py-2.5 text-center text-sm font-semibold text-white disabled:opacity-50"
                  style={{ background: 'var(--ss-accent, #2563EB)' }}
                >
                  {send.isPending ? 'Sending…' : 'Send review request'}
                </button>
              </div>
            </div>
          ) : (
            /* Recipient picker */
            <>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-ink-500 dark:text-[#94A3B8]">
                  Search recent customers
                </span>
                <input
                  type="text"
                  value={search}
                  placeholder="Name or number"
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-[15px] text-ink-900 placeholder:text-ink-400 dark:border-[#334155] dark:bg-[#1E293B] dark:text-[#E2E8F0]"
                />
              </label>

              <ul className="mt-2 divide-y divide-ink-100 dark:divide-[#1F2A37]">
                {recentQuery.isLoading ? (
                  <li className="py-3 text-center text-[13px] text-ink-500 dark:text-[#94A3B8]">
                    Loading recent customers…
                  </li>
                ) : recipients.length === 0 ? (
                  <li className="py-3 text-center text-[13px] text-ink-500 dark:text-[#94A3B8]">
                    No matches — enter a number below.
                  </li>
                ) : (
                  recipients.slice(0, 20).map((r) => (
                    <li key={r.contactId}>
                      <button
                        type="button"
                        onClick={() => setPicked(r)}
                        className="ss-press flex w-full items-center justify-between px-1 py-2.5 text-left"
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-[15px] font-medium text-ink-900 dark:text-[#E2E8F0]">
                            {r.name}
                          </span>
                          {r.phone ? (
                            <span className="block text-xs text-ink-500 dark:text-[#94A3B8]">
                              {r.phone}
                            </span>
                          ) : null}
                        </span>
                        <Star className="ml-2 h-4 w-4 flex-shrink-0 text-amber-400" aria-hidden="true" />
                      </button>
                    </li>
                  ))
                )}
              </ul>

              <div className="mt-3 border-t border-ink-100 pt-3 dark:border-[#1F2A37]">
                <span className="mb-1 block text-xs font-semibold text-ink-500 dark:text-[#94A3B8]">
                  Or enter a phone number
                </span>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={manualPhone}
                    placeholder="(203) 555-0123"
                    onChange={(e) => setManualPhone(e.target.value)}
                    className="min-w-0 flex-1 rounded-xl border border-ink-200 bg-white px-3 py-2 text-[15px] text-ink-900 placeholder:text-ink-400 dark:border-[#334155] dark:bg-[#1E293B] dark:text-[#E2E8F0]"
                  />
                  <button
                    type="button"
                    disabled={!manualValid}
                    onClick={() =>
                      setPicked({ kind: 'phone', phone: manualPhone.trim(), name: manualPhone.trim() })
                    }
                    className="ss-press rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
                    style={{ background: 'var(--ss-accent, #2563EB)' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
